import React from 'react';
import { Badge } from '../components/ui/badge';
import { Globe, Smartphone, Activity, Box, ArrowRight } from 'lucide-react';

const FEATURES = [
  {
    icon: Globe,
    title: "Anytime, Anywhere Inspections",
    description: "Inspection can be done anytime & anywhere globally with full traceability of aircraft assets"
  },
  {
    icon: Smartphone,
    title: "Hardware Agnostic",
    description: "Use iPhone, Android, drones & iPad — no expensive custom hardware required"
  },
  {
    icon: Activity,
    title: "Real-time Guidance",
    description: "Platform guides users with instant issue detection for better decision making"
  },
  {
    icon: Box,
    title: "Digital Twin",
    description: "Create precise 3D digital replicas of aircraft for virtual inspections, damage tracking, and historical comparison analysis"
  }
];

const WORKFLOW_STEPS = [
  {
    image: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/vbor9svy_Screenshot%202026-08-23%20at%204.32.22%E2%80%AFPM.png",
    alt: "ArcheanX Inspection Intelligence for Aviation",
    title: "Scan & Upload the video",
    description: "Capture aircraft exterior footage",
    imgClass: "w-80 h-48 object-cover"
  },
  {
    image: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/xugfj7p0_Screenshot%202026-08-23%20at%204.32.45%E2%80%AFPM.webp",
    alt: "Analysis Results Workspace",
    title: "Inspection Workspace analysis results",
    description: "Computer vision detects anomalies",
    imgClass: "w-80 h-48 object-cover"
  },
  {
    image: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/a0jhoo3i_Screenshot%202026-08-23%20at%204.37.53%E2%80%AFPM.png",
    alt: "FAA Form 8130-3 Authorized Release Certificate Report",
    title: "Report",
    description: "FAA-aligned inspection reports",
    imgClass: "w-80 h-48 object-cover object-top"
  },
  {
    image: "https://customer-assets-jt897jd0.emergentagent.net/job_dbbb4f88-5ac8-4fde-9c1a-a326bcf136ff/artifacts/fxika0pf_ArcheanX%20Page_Traceability.webp",
    alt: "Inspection Health Dashboard - Findings Over Time",
    title: "Inspection Health",
    description: "FAA compliant documentation",
    imgClass: "w-80 h-48 object-cover"
  }
];

export const PlatformSection = () => {
  return (
    <section id="platform" data-testid="platform-section" className="py-20 px-6 bg-white">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black">ArcheanX Platform</h2>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Smarter inspections. Faster decisions. Safer flights.
          </p>
        </div>

        {/* Features */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <Badge className="bg-orange-100 text-orange-700 border-orange-200">
              Features
            </Badge>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-x-16 gap-y-8">
              {FEATURES.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-black mb-1">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Video Workflow: Original → Screenshot → Digital Twin */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <Badge className="bg-orange-100 text-orange-700 border-orange-200">
              Inspection Workflow
            </Badge>
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-0">
            {WORKFLOW_STEPS.map((step, idx) => (
              <React.Fragment key={idx}>
                <div className="flex flex-col items-center text-center max-w-sm">
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 mb-4">
                    <img src={step.image} alt={step.alt} className={step.imgClass} />
                  </div>
                  <h3 className="text-lg font-bold text-black mb-1">{step.title}</h3>
                  <p className="text-sm text-gray-700">{step.description}</p>
                </div>
                {idx < WORKFLOW_STEPS.length - 1 && (
                  <>
                    <div className="hidden lg:flex items-center px-6">
                      <ArrowRight className="w-8 h-8 text-orange-500" />
                    </div>
                    <div className="lg:hidden py-2">
                      <ArrowRight className="w-8 h-8 text-orange-500 rotate-90" />
                    </div>
                  </>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Footer Text */}
        <p className="text-center text-sm text-gray-600 max-w-2xl mx-auto">
          Professional FAA Part 43 compliant aircraft inspection with AI-powered anomaly detection and comprehensive reporting.
        </p>
      </div>
    </section>
  );
};
